import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { DeviceType, KeyboardShortcutType, } from '#pplx/frontend/apps/ask/keyboards/keyboards-core/consts.js';
import { useKeyboardShortcuts } from '#pplx/frontend/apps/ask/keyboards/useKeyboardShortcuts.js';
import { convertDisplayToKey, convertKeyToDisplay, } from '#pplx/frontend/apps/ask/keyboards/utils.js';
import { Overlay } from '#pplx/frontend/components/overlays/Overlay.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const ShortcutRow = memo(function ShortcutRow({ label, keys, isEditing, onEdit, onSave, onCancel, editable = true, shortcuts, shortcutType, device, }) {
    const { $t } = useIntl();
    const [error, setError] = useState(undefined);
    const [currentKeys, setCurrentKeys] = useState([]);
    const inputRef = useRef(null);
    const checkForDuplicates = useCallback((keys) => {
        const sortedKeys = [...keys].sort();
        const isDuplicate = Object.entries(shortcuts)
            .filter(([type]) => type !== shortcutType)
            .some(([_, deviceShortcuts]) => deviceShortcuts[device].length === keys.length && // Same number of keys
            // Sort both arrays to compare keys regardless of order
            [...deviceShortcuts[device]].sort().every((key, index) => key === sortedKeys[index]));
        if (isDuplicate) {
            setError($t({
                defaultMessage: 'This shortcut is already in use',
                description: 'Error message when user tries to assign a keyboard shortcut that is already used',
                id: 'a52130815a',
            }));
            return true;
        }
        setError(undefined);
        return false;
    }, [$t, shortcuts, shortcutType, device]);
    const handleKeyDown = useCallback((e) => {
        // Prevent all default key behavior (e.g., Escape closing the modal, Space scrolling) — capture the key press as a shortcut assignment instead
        e.preventDefault();
        e.stopPropagation();
        // Ignore CapsLock key
        if (e.key === 'CapsLock') {
            return;
        }
        const keys = [];
        if (e.metaKey) {
            if (device === DeviceType.WINDOWS) {
                keys.push('Win');
            }
            else {
                keys.push('⌘');
            }
        }
        if (e.ctrlKey)
            keys.push('Ctrl');
        if (e.altKey)
            keys.push('Alt');
        if (e.shiftKey)
            keys.push('⇧');
        // Add space as a modifier if it's being held down
        if (e.code === 'Space' && e.key === ' ') {
            keys.push('Space');
            return; // Return here to wait for the next key
        }
        // Add the main key if it's not a modifier
        const key = e.key;
        if (!['Meta', 'Control', 'Alt', 'Shift', 'Win'].includes(key)) {
            // Special handling for Escape key
            if (key === 'Escape') {
                onCancel();
                return;
            }
            // Capitalize single letters for consistency
            if (key.length === 1) {
                keys.push(key.toUpperCase());
            }
            else {
                // Use the display version of special keys
                keys.push(convertKeyToDisplay(key, device));
            }
        }
        if (keys.length > 0) {
            let newKeys;
            // If we already have edited keys (from space being pressed), append the new key
            if (currentKeys.length > 0 && currentKeys[0] === 'Space') {
                newKeys = [...currentKeys, ...keys];
            }
            else {
                newKeys = keys;
            }
            setCurrentKeys(newKeys);
        }
    }, [currentKeys, onCancel, device]);
    const handleKeyUp = useCallback((e) => {
        // Check if all modifier keys are released
        const allKeysReleased = !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey;
        // Only process if we have keys and all modifier keys are released
        if (currentKeys.length > 0 && allKeysReleased) {
            // Check for minimum number of keys
            if (currentKeys.length < 2) {
                setError($t({
                    defaultMessage: 'Please use at least 2 keys for the shortcut',
                    description: 'Error message when user tries to set a keyboard shortcut with fewer than 2 keys',
                    id: '1711c6c700',
                }));
                return;
            }
            // Check if there's at least one letter or non-modifier key
            const hasNonModifier = currentKeys.some((key) => !['⌘', 'Ctrl', 'Alt', '⇧', 'Space', 'Win', 'Meta'].includes(key));
            if (!hasNonModifier) {
                setError($t({
                    defaultMessage: 'Please include at least one letter or non-modifier key',
                    description: 'Error message when user tries to set a keyboard shortcut without any non-modifier keys',
                    id: 'b2104e2a62',
                }));
                return;
            }
            // Check for duplicates before setting the keys
            if (!checkForDuplicates(currentKeys)) {
                // Convert display icons back to actual key values before saving
                const convertedKeys = currentKeys.map((key) => {
                    if (key === 'Win') {
                        return 'Meta';
                    }
                    return convertDisplayToKey(key);
                });
                onSave(convertedKeys);
                // Remove focus from input
                inputRef.current?.blur();
            }
            setCurrentKeys([]);
        }
    }, [$t, currentKeys, checkForDuplicates, onSave]);
    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
        }
    }, [isEditing]);
    const handleCancel = useCallback(() => {
        setCurrentKeys([]);
        setError(undefined);
        onCancel();
    }, [onCancel]);
    const renderedEditedKeys = useMemo(() => currentKeys.map((k) => (_jsx(BoxPrimitive, { display: "flex", align: "center", justify: "center", className: "px-1 h-5 min-w-5 rounded-sm border font-mono", children: convertKeyToDisplay(k, device) }, k))), [currentKeys, device]);
    if (isEditing) {
        return (_jsxs(TextPrimitive, { color: "light", className: "py-4 flex place-content-between items-center", children: [_jsx(TextPrimitive, { as: "dd", variant: "small", color: "light", lineClamp: 1, className: "overflow-hidden text-clip", children: label }), _jsxs(BoxPrimitive, { display: "flex", direction: "col", align: "end", children: [_jsxs(BoxPrimitive, { display: "flex", align: "center", rounded: "md", ref: inputRef, as: "div", tabIndex: 0, onKeyDown: handleKeyDown, onKeyUp: handleKeyUp, className: `border-super-strong bg-base px-2 py-1 gap-x-two focus:ring-super-strong min-w-[150px] border-2 shadow-sm focus:outline-hidden focus:ring-2 focus:ring-offset-2 ${error ? 'border-negative-strong' : ''}`, children: [_jsx(BoxPrimitive, { display: "flex", align: "center", className: "gap-x-two flex-1", children: currentKeys.length > 0 ? (renderedEditedKeys) : (_jsx(TextPrimitive, { variant: "small", children: _jsx(FormattedMessage, { defaultMessage: "Press keys to record shortcut...", description: "Placeholder text in the keyboard shortcut editor input when waiting for user to press keys", id: "cad1b4302d" }) })) }), currentKeys.length > 0 && (_jsx(BoxPrimitive, { as: "button", onClick: handleCancel, className: "text-secondary hover:text-primary ml-auto", "aria-label": $t({
                                        defaultMessage: 'Clear shortcut',
                                        description: 'Accessibility label for the button to clear the current keyboard shortcut input',
                                        id: '1cb5c9a520',
                                    }), children: "\u2715" }))] }), error && (_jsx(TextPrimitive, { variant: "tiny", color: "caution", className: "mt-1", children: error }))] })] }));
    }
    return (_jsxs(TextPrimitive, { color: "light", className: "py-4 flex place-content-between items-center", children: [_jsx(TextPrimitive, { as: "dd", variant: "small", color: "light", lineClamp: 1, className: "overflow-hidden text-clip", children: label }), _jsxs(BoxPrimitive, { padding: "sm", display: "flex", align: "center", rounded: "lg", as: editable ? 'button' : 'dl', onClick: editable ? onEdit : undefined, hoverShadow: editable ? 'sm' : undefined, className: `gap-x-1 transition-all duration-150 ${editable ? 'hover:border-super-strong cursor-pointer border border-dashed' : ''}`, children: [keys.map((k) => (_jsx(BoxPrimitive, { display: "flex", align: "center", justify: "center", className: "px-1 h-5 min-w-5 rounded-sm border font-mono", children: convertKeyToDisplay(k, device) }, k))), editable && (_jsx(TextPrimitive, { variant: "tiny", color: "light", className: "ml-1", children: _jsx(FormattedMessage, { defaultMessage: "Click to edit", description: "Hint text next to a keyboard shortcut indicating it can be clicked to edit", id: "c44d56071c" }) }))] })] }));
});
export const KeyboardShortcutEditorModal = memo(function KeyboardShortcutEditorModal({ onClose }) {
    const { device: { isMacOS }, } = useWebPlatform();
    const { setShortcut, globalShortcuts, defaultGlobalShortcuts } = useKeyboardShortcuts();
    const { $t } = useIntl();
    const device = isMacOS ? DeviceType.MACOS : DeviceType.WINDOWS;
    const [editingShortcut, setEditingShortcut] = useState(undefined);
    const handleEdit = useCallback((shortcut) => {
        setEditingShortcut(shortcut.type);
    }, []);
    const handleSave = useCallback(async (shortcut, convertedKeys) => {
        // Check for duplicate keychords
        const isDuplicate = Object.entries(globalShortcuts)
            .filter(([type]) => type !== shortcut.type)
            .some(([, keysByDevice]) => {
            const keys = keysByDevice[device];
            return (keys.length === convertedKeys.length &&
                [...keys].sort().every((key, index) => key === [...convertedKeys].sort()[index]));
        });
        if (isDuplicate) {
            logger.warn(`Cannot set duplicate keychord for ${shortcut.type}: ${convertedKeys.join('+')}`);
            return;
        }
        // Update global shortcut for both newThread and v2v shortcuts
        if (shortcut.type === KeyboardShortcutType.NEW_THREAD ||
            shortcut.type === KeyboardShortcutType.TOGGLE_V2V) {
            // Update the Zustand store & LS
            setShortcut(shortcut.type, device, convertedKeys);
        }
        setEditingShortcut(undefined);
    }, [device, setShortcut, globalShortcuts]);
    const handleCancel = useCallback((_shortcut) => {
        setEditingShortcut(undefined);
    }, []);
    const shortcutRows = useMemo(() => defaultGlobalShortcuts.map((shortcut) => (_jsx(ShortcutRowFactory, { shortcut: {
            ...shortcut,
            macos: globalShortcuts[shortcut.type]?.[DeviceType.MACOS].length
                ? (globalShortcuts[shortcut.type]?.[DeviceType.MACOS] ?? shortcut.macos)
                : shortcut.macos,
            windows: globalShortcuts[shortcut.type]?.[DeviceType.WINDOWS].length
                ? (globalShortcuts[shortcut.type]?.[DeviceType.WINDOWS] ?? shortcut.windows)
                : shortcut.windows,
        }, device: device, editingShortcut: editingShortcut, handleCancel: handleCancel, handleEdit: handleEdit, handleSave: handleSave, shortcuts: globalShortcuts }, $t(shortcut.label)))), [
        device,
        editingShortcut,
        handleCancel,
        handleEdit,
        handleSave,
        $t,
        defaultGlobalShortcuts,
        globalShortcuts,
    ]);
    return (_jsx(Overlay, { childVariant: "hide-chrome", isOpen: true, canOutsideClickClose: true, onClose: onClose, children: _jsx(BoxPrimitive, { rounded: "xl", bg: "background", className: "relative w-[90vw] max-w-screen-sm overflow-hidden", children: _jsxs(BoxPrimitive, { bg: "subtle", padding: "md", className: "inset-0", children: [_jsx(TextPrimitive, { className: "mb-2", variant: "section-title", as: "h2", children: _jsx(FormattedMessage, { defaultMessage: "Keyboard Shortcuts", description: "Heading for the keyboard shortcut editor modal", id: "714ffe6060" }) }), _jsx(TextPrimitive, { color: "light", className: "mb-4", children: _jsx(FormattedMessage, { defaultMessage: "Edit keyboard shortcuts by clicking on them below. Some shortcuts cannot be modified for system functionality.", description: "Instructional text in the keyboard shortcut editor modal", id: "15b8701c92" }) }), _jsx(BoxPrimitive, { as: "dl", className: "divide-y", children: shortcutRows })] }) }) }));
});
const ShortcutRowFactory = React.memo(function ShortcutRowFactory({ shortcut, device, editingShortcut, handleCancel, handleEdit, handleSave, shortcuts, }) {
    const { $t } = useIntl();
    const onEdit = useCallback(() => handleEdit(shortcut), [handleEdit, shortcut]);
    const onSave = useCallback((convertedKeys) => handleSave(shortcut, convertedKeys), [handleSave, shortcut]);
    const onCancel = useCallback(() => handleCancel(shortcut), [handleCancel, shortcut]);
    return (_jsx(ShortcutRow, { label: $t(shortcut.label), keys: shortcut[device], isEditing: editingShortcut === shortcut.type, onEdit: onEdit, onSave: onSave, onCancel: onCancel, editable: shortcut.editable, shortcuts: shortcuts, shortcutType: shortcut.type, device: device }));
});
//# sourceMappingURL=KeyboardShortcutEditorModal.js.map